import { makeStyles } from '@material-ui/core/styles'

const COLORS = {
  primary: '#6064FF',
  primaryHover: '#4e52e0',
  textDark: '#111',
  textMedium: '#222',
  textBody: '#333',
  textLabel: '#444',
  textOptional: '#888',
  disabled: '#e0e0e0',
  disabledText: '#a0a0a0',
  border: '#e0e0e0',
  summaryBg: '#FBFBFB',
  infoBg: '#DBDCFF',
  readOnlyBg: '#f5f5f5',
  successIcon: '#3AD399',
  errorIcon: '#F35B69',
  invalidIcon: '#000000',
  white: '#fff',
}

const STATUS_ICON_SIZE = 56
const STATUS_ICON_SIZE_MOBILE = 48
const STATUS_ICON_FONT = 30
const STATUS_ICON_FONT_MOBILE = 24

const statusIconBase = {
  borderRadius: '50%',
  width: STATUS_ICON_SIZE,
  height: STATUS_ICON_SIZE,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 16,
}

const statusIconMobile = (theme) => ({
  [theme.breakpoints.down('xs')]: {
    width: STATUS_ICON_SIZE_MOBILE,
    height: STATUS_ICON_SIZE_MOBILE,
    marginBottom: 12,
  },
})

export default makeStyles((theme) => ({
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 0,
    [theme.breakpoints.down('xs')]: {
      padding: '12px 16px 0',
    },
  },
  closeOnlyTitleRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    [theme.breakpoints.down('xs')]: {
      padding: '8px 12px 0',
    },
  },
  title: {
    fontWeight: 700,
    color: COLORS.textDark,
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.1rem',
    },
  },
  titleWithSpacing: {
    marginBottom: 8,
  },
  dialogContentVisibleOverflow: {
    overflowX: 'visible',
  },
  subtitle: {
    marginBottom: 16,
    color: COLORS.textLabel,
    fontWeight: 500,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.8rem',
      marginBottom: 12,
    },
  },
  fieldLabel: {
    marginBottom: 4,
    fontWeight: 600,
    color: COLORS.textMedium,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.8rem',
    },
  },
  optionalLabel: {
    fontWeight: 400,
    color: COLORS.textOptional,
  },
  field: {
    marginBottom: 16,
    [theme.breakpoints.down('xs')]: {
      marginBottom: 12,
    },
  },
  readOnlyField: {
    marginBottom: 16,
    backgroundColor: COLORS.readOnlyBg,
    [theme.breakpoints.down('xs')]: {
      marginBottom: 12,
    },
  },
  multilineField: {
    marginBottom: 24,
    [theme.breakpoints.down('xs')]: {
      marginBottom: 16,
    },
  },
  multilineInput: {
    height: 'auto',
    minHeight: 96,
    padding: '12px 14px',
    alignItems: 'flex-start',
    boxSizing: 'border-box',
    [theme.breakpoints.down('xs')]: {
      minHeight: 80,
      padding: '10px 12px',
    },
  },
  multilineTextarea: {
    padding: '8px 0 0 0',
    resize: 'none',
    lineHeight: 1.5,
    boxSizing: 'border-box',
  },
  helperText: {
    marginLeft: 0,
    marginRight: 0,
    textAlign: 'right',
  },
  btnOutlined: {
    borderRadius: 50,
    minWidth: 120,
    padding: '9px 23px',
    borderColor: COLORS.primary,
    color: COLORS.primary,
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': {
      borderColor: COLORS.primaryHover,
      backgroundColor: 'rgba(96, 100, 255, 0.04)',
    },
    [theme.breakpoints.down('xs')]: {
      minWidth: 100,
      padding: '7px 17px',
      fontSize: '0.8rem',
    },
  },
  btnContained: {
    borderRadius: 50,
    minWidth: 120,
    padding: '10px 24px',
    textTransform: 'none',
    fontWeight: 600,
    backgroundColor: COLORS.primary,
    '&:hover': {
      backgroundColor: COLORS.primaryHover,
    },
    '&.Mui-disabled': {
      backgroundColor: COLORS.disabled,
      color: COLORS.disabledText,
    },
    [theme.breakpoints.down('xs')]: {
      minWidth: 100,
      padding: '8px 18px',
      fontSize: '0.8rem',
    },
  },
  btnContainedWide: {
    minWidth: 160,
    [theme.breakpoints.down('xs')]: {
      minWidth: 130,
    },
  },
  summaryBox: {
    backgroundColor: COLORS.summaryBg,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    [theme.breakpoints.down('xs')]: {
      padding: 12,
      marginBottom: 12,
    },
  },
  summaryLabel: {
    color: COLORS.textLabel,
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 2,
    [theme.breakpoints.down('xs')]: {
      fontSize: 12,
      lineHeight: 1.8,
    },
  },
  summaryValue: {
    color: COLORS.textDark,
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 2,
    wordBreak: 'break-all',
    [theme.breakpoints.down('xs')]: {
      fontSize: 12,
      lineHeight: 1.8,
    },
  },
  infoBox: {
    backgroundColor: COLORS.infoBg,
    borderRadius: 8,
    padding: 12,
    marginBottom: 24,
    display: 'flex',
    alignItems: 'flex-start',
    [theme.breakpoints.down('xs')]: {
      padding: 10,
      marginBottom: 16,
    },
  },
  infoIcon: {
    color: COLORS.textBody,
    fontSize: 20,
    marginRight: 8,
    flexShrink: 0,
    marginTop: 2,
  },
  infoText: {
    color: COLORS.textMedium,
    fontWeight: 700,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.75rem',
    },
  },
  actionsSpacing: {
    marginBottom: 16,
  },
  statusContent: {
    padding: '8px 0 32px',
    [theme.breakpoints.down('xs')]: {
      padding: '8px 0 20px',
    },
  },
  successContent: {
    padding: '48px 0 40px',
    [theme.breakpoints.down('xs')]: {
      padding: '32px 0 28px',
    },
  },
  successIcon: {
    ...statusIconBase,
    backgroundColor: COLORS.successIcon,
    ...statusIconMobile(theme),
  },
  errorIcon: {
    ...statusIconBase,
    backgroundColor: COLORS.errorIcon,
    ...statusIconMobile(theme),
  },
  invalidIcon: {
    ...statusIconBase,
    backgroundColor: COLORS.invalidIcon,
    ...statusIconMobile(theme),
  },
  statusIconSymbol: {
    color: COLORS.white,
    fontSize: STATUS_ICON_FONT,
    [theme.breakpoints.down('xs')]: {
      fontSize: STATUS_ICON_FONT_MOBILE,
    },
  },
  bodySpacing: {
    marginBottom: 24,
    color: COLORS.textBody,
    fontWeight: 500,
    [theme.breakpoints.down('xs')]: {
      marginBottom: 16,
      fontSize: '0.8rem',
    },
  },
}))
